import styled, { x } from '@xstyled/styled-components';
import React, { useState } from 'react';
import { msgid, ngettext, t } from 'ttag';
import { PretestSkipRule } from '~/data';
import { select } from '~/lib/select';
import { Flex } from '~/ui/flex/Flex';
import { TrainingState, useTrainingStore } from '../state';
import { PretestButton, PretestScreen, SecondaryButton } from './PretestScreen';

const Rule = styled.hr`
  width: 100%;
  margin: 0;
  border: 0;
  border-top: 1px solid ${p => p.theme.colors.greyMed};
`;

const Muted = styled.div`
  display: flex;
  flex-direction: column;
  gap: sm;
  color: var(--color-grey-dk);

  p {
    margin: 0;
  }
`;

const Facts = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  margin: 0;
  padding: 0;
  list-style: none;
  font-family: ralewayBold;
  font-size: 14px;
`;

const selector = (s: TrainingState) =>
  select(
    s,
    'pretest',
    'title',
    'featureShowCertificate',
    'startPretest',
    'declinePretest'
  );

/**
 * The invitation to test out, before the assessment starts, and what it can do: complete the course (and earn
 * the certificate) only when the authored assessment covers every chapter; otherwise skip the chapters it
 * covers. Under the passing-score rule it names the passing score the offer carries. Under the strict rule
 * there is none: a chapter is skipped when every answer about it is right. It never falls back to the course's
 * own passing score, which reads 0 when the course has none.
 */
export const PretestIntro = () => {
  const {
    pretest,
    title,
    featureShowCertificate,
    startPretest,
    declinePretest
  } = useTrainingStore(selector);
  const [waiting, setWaiting] = useState(false);
  const questions = pretest?.questionCount ?? 0;
  const covered = pretest?.testableChapterCount ?? 0;
  const passing = pretest?.passingScorePercent ?? null;
  const scored =
    pretest?.skipRule === PretestSkipRule.PassingScore && passing != null;
  const wholeCourse = Boolean(pretest?.fullCourseEligible);

  // The pretest page follows the pretest status, so once the server accepts the learner's choice the next
  // screen takes over. Until it answers, the learner cannot choose twice.
  const choose = (action: () => Promise<boolean>) => async () => {
    setWaiting(true);
    if (!(await action())) setWaiting(false);
  };

  let reward = scored
    ? t`Score ${passing}% or higher to skip every chapter this assessment covers.`
    : t`Skip each chapter this assessment covers by answering every question about it correctly.`;
  if (wholeCourse && scored) {
    reward = featureShowCertificate
      ? t`Score ${passing}% or higher to complete this course without taking the lessons and earn your completion certificate.`
      : t`Score ${passing}% or higher to complete this course without taking the lessons.`;
  } else if (wholeCourse) {
    reward = featureShowCertificate
      ? t`Answer every question correctly to complete this course without taking the lessons and earn your completion certificate.`
      : t`Answer every question correctly to complete this course without taking the lessons.`;
  }
  // Under the strict rule, an assessment that covers only some chapters has said it all in one line.
  let fallback: string | null = null;
  if (scored) {
    fallback = t`If you score lower, you’ll still skip each chapter where you answer every question correctly, and take the rest.`;
  } else if (wholeCourse) {
    fallback = t`If you miss a question, you’ll still skip each chapter where you answer every question correctly, and take the rest.`;
  }

  return (
    <PretestScreen wide>
      <x.h2 fontFamily="llama" m={0}>
        {title}
      </x.h2>
      <x.p m={0}>
        {t`Good news — you can test out of this training. Take a short knowledge assessment to show what you already know.`}
      </x.p>
      <Rule />
      {/* Raleway only loads its regular weight, so bold text uses ralewayBold. */}
      <x.h3 fontFamily="ralewayBold" fontWeight="normal" fontSize="lg" m={0}>
        {t`How it works`}
      </x.h3>
      <Muted>
        <p>{reward}</p>
        {fallback && <p>{fallback}</p>}
      </Muted>
      <Facts>
        <li>
          {ngettext(
            msgid`${questions} question`,
            `${questions} questions`,
            questions
          )}
        </li>
        <li>
          {ngettext(msgid`${covered} chapter`, `${covered} chapters`, covered)}
        </li>
        <li>{t`Graded when you submit`}</li>
      </Facts>
      <Rule />
      <Flex flexDirection="column" gap="sm">
        <PretestButton
          w="100%"
          onClick={choose(startPretest)}
          disabled={waiting}
          data-test-id="start-pretest-button"
        >
          {t`Begin knowledge assessment`}
        </PretestButton>
        <SecondaryButton
          w="100%"
          onClick={choose(declinePretest)}
          disabled={waiting}
          data-test-id="skip-pretest-button"
        >
          {t`Skip assessment and proceed to course`}
        </SecondaryButton>
      </Flex>
    </PretestScreen>
  );
};
